<template>
  <!-- One modal for the appointment actions (Android: AppointmentCancelReasonFragment, DisposeLeadFragment,
       ResumeLeadDialog, AppointmentNotesFragment, FamilyMembersDialog, create-order web flow) -->
  <div ref="apptActionModal" class="modal fade fxModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static"
    data-bs-keyboard="false">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable mobile:mx-[16px] tablet:mx-auto"
      :class="type === 'frame' ? 'max-w-full tablet:!max-w-[90vw] largeDesktop:!max-w-[1200px]' : 'max-w-full tablet:!max-w-[480px]'">
      <div class="modal-content" :class="{ '!min-h-[60vh] !max-h-[90vh]': listLayout }">
        <!-- members / notes: header stays put, only the list scrolls (keeps the 15px gap under the header visible) -->
        <div class="modal-body !p-[15px] font-roboto" :class="{ 'flex flex-col !overflow-hidden': listLayout }">
          <FxModalHeader :title="title" :dismiss="false" @close="close" />

          <!-- server-hosted page (create / finalize order, quote summary) -->
          <template v-if="type === 'frame'">
            <iframe v-if="url" :src="url" :title="title" class="w-full h-[75vh] border-0 rounded-[8px] mt-[15px]" />
            <div class="flex items-center justify-between gap-2.5 mt-[10px]">
              <p class="m-0 text-[13px] leading-[16px] text-[#666666]">
                {{ frameHint }}
              </p>
              <button type="button" class="fxBtnSecondary" @click="close">
                Close
              </button>
            </div>
          </template>

          <!-- cancel appointment -->
          <template v-else-if="type === 'cancel'">
            <div class="flex flex-col gap-[10px] mt-[15px]">
              <label class="apptLabel" for="apptCancelReason">Reason <span class="text-[#EE5851]">*</span></label>
              <FxSelect v-model="form.reason" :options="cancelReasonOptions" placeholder="Select a reason" inline />
              <template v-if="isOther(form.reason)">
                <label class="apptLabel" for="apptCancelOther">Other reason <span class="text-[#EE5851]">*</span></label>
                <textarea id="apptCancelOther" v-model="form.notes" maxlength="500" rows="3" class="apptInput !h-auto py-[8px]" />
              </template>
            </div>
            <!-- confirm inline: the global $dialog would stack under this Bootstrap modal -->
            <p v-if="form.confirming" class="m-0 mt-[20px] text-[15px] text-center text-[#333333]">
              Are you sure you want to cancel this appointment?
            </p>
            <div v-if="form.confirming" class="flex justify-center gap-[10px] mt-[15px]">
              <button type="button" class="fxDeclineCancel" @click="form.confirming = false">
                No
              </button>
              <button type="button" class="fxBtnPrimary" :disabled="busy" @click="submitCancel">
                Yes
              </button>
            </div>
            <div v-else class="flex justify-center gap-[10px] mt-[20px]">
              <button type="button" class="fxDeclineCancel" @click="close">
                Close
              </button>
              <button type="button" class="fxBtnPrimary"
                :disabled="busy || !form.reason || (isOther(form.reason) && !form.notes.trim())" @click="form.confirming = true">
                Cancel Appointment
              </button>
            </div>
          </template>

          <!-- disposition -->
          <template v-else-if="type === 'dispose'">
            <div class="flex flex-col gap-[10px] mt-[15px]">
              <template v-if="appointment.notes">
                <span class="apptLabel">Appointment Notes</span>
                <p class="m-0 text-[14px] text-[#333333] whitespace-pre-line break-words">
                  {{ appointment.notes }}
                </p>
              </template>
              <label class="apptLabel" for="apptDisposeReason">Reason <span class="text-[#EE5851]">*</span></label>
              <FxSelect v-model="form.reasonId" :options="disposeReasons" label-key="name" value-key="id"
                placeholder="Select a reason" inline />
              <template v-if="disposeIsOther">
                <label class="apptLabel" for="apptDisposeNotes">Reason details <span class="text-[#EE5851]">*</span></label>
                <textarea id="apptDisposeNotes" v-model="form.notes" maxlength="1000" rows="3" class="apptInput !h-auto py-[8px]" />
              </template>
            </div>
            <div class="flex justify-center gap-[10px] mt-[20px]">
              <button type="button" class="fxDeclineCancel" @click="close">
                Cancel
              </button>
              <button type="button" class="fxBtnPrimary"
                :disabled="busy || !form.reasonId || (disposeIsOther && !form.notes.trim())" @click="submitDispose">
                Dispositioned Lead
              </button>
            </div>
          </template>

          <!-- resume -->
          <template v-else-if="type === 'resume'">
            <p class="m-0 mt-[15px] text-[14px] text-[#333333]">
              Please provide a reason for resuming this Lead.
            </p>
            <textarea v-model="form.notes" maxlength="1000" rows="4" class="apptInput !h-auto py-[8px] mt-[10px]" placeholder="Notes" />
            <div class="flex justify-center gap-[10px] mt-[20px]">
              <button type="button" class="fxDeclineCancel" @click="close">
                Cancel
              </button>
              <button type="button" class="fxBtnPrimary" :disabled="busy || !form.notes.trim()" @click="submitResume">
                Submit
              </button>
            </div>
          </template>

          <!-- family members -->
          <template v-else-if="type === 'members'">
            <ul class="list-none m-0 p-0 flex-1 min-h-0 flex flex-col gap-[10px] overflow-y-auto">
              <li v-if="!members.length" class="text-center text-[13px] text-[#999999]">
                No members found
              </li>
              <li v-for="(m, index) in members" :key="m.id || index"
                class="flex items-center gap-[12px] px-[12px] py-[10px] rounded-[10px] border-1 border-[#E9ECF1] bg-white">
                <span class="w-[36px] h-[36px] rounded-full flex-shrink-0 flex items-center justify-center text-[15px] font-medium text-white"
                  :style="{ backgroundColor: ['#7695FF', '#14B8A6', '#F5A524', '#F67280', '#A084E8'][index % 5] }">
                  {{ (m.first_name || m.full_name || '?').charAt(0).toUpperCase() }}
                </span>
                <div class="flex-1 min-w-0">
                  <p class="m-0 text-[15px] font-medium text-[#2E3138] break-words">
                    {{ m.full_name || [m.first_name, m.last_name].filter(Boolean).join(' ') }}
                  </p>
                  <p class="m-0 text-[12px] text-[#8A9099]">
                    {{ m.relation }}<template v-if="m.phone">
                      · {{ m.phone }}
                    </template>
                  </p>
                </div>
                <a v-if="m.is_allowed && m.phone" :href="'tel:' + m.phone" aria-label="Call member"
                  class="w-[32px] h-[32px] rounded-full flex-shrink-0 flex items-center justify-center bg-[#F67280]">
                  <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#FFFFFF"
                      d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z" />
                  </svg>
                </a>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import moment from "moment";
import { useToast } from "vue-toastification";
import FxModalHeader from "../nest_components/FxModalHeader.vue";
import FxSelect from "../partialComponent/FxSelect.vue";
import { appointmentApi } from "@/services/appointmentApi";
import { disableScroll, enableScroll } from "../../helpers/scrollHelper";

const toast = useToast();

export default {
  components: { FxModalHeader, FxSelect },
  props: {
    appointment: { type: Object, required: true },
  },
  // changed: reload the appointment; closed(type): the modal was hidden
  emits: ["changed", "cancelled", "closed"],
  data() {
    return {
      type: "",
      title: "",
      url: "",
      frameHint: "",
      busy: false,
      form: { notes: "" },
      cancelReasons: [],
      disposeReasons: [],
      members: [],
    };
  },
  computed: {
    id() {
      return this.appointment?.id;
    },
    // members and plain notes: at least half the screen, at most the screen, the list scrolls inside
    listLayout() {
      return this.type === "members" || this.type === "notes";
    },
    cancelReasonOptions() {
      return this.cancelReasons.map(r => ({ label: r, value: r }));
    },
    disposeIsOther() {
      return Number(this.disposeReasons.find(r => r.id === this.form.reasonId)?.is_other) === 1;
    },
  },
  mounted() {
    this.bsModal = new Modal(this.$refs.apptActionModal);
    this.$refs.apptActionModal.addEventListener("hidden.bs.modal", this.onHidden);
  },
  beforeUnmount() {
    this.$refs.apptActionModal?.removeEventListener("hidden.bs.modal", this.onHidden);
    this.bsModal?.dispose();
  },
  methods: {
    show(type, title, form = {}) {
      this.type = type;
      this.title = title;
      this.form = { notes: "", ...form };
      disableScroll();
      this.bsModal.show();
    },
    close() {
      this.bsModal?.hide();
    },
    onHidden() {
      enableScroll();
      const type = this.type;
      this.type = "";
      this.url = "";
      this.$emit("closed", type);
    },
    isOther(reason) {
      return String(reason || "").toLowerCase() === "other";
    },
    formatTime(val) {
      const date = /^\d+$/.test(String(val)) ? moment.unix(Number(val)) : moment(val);
      return val && date.isValid() ? date.format("hh:mm a MM/DD/YYYY") : "";
    },

    // ---- openers (called by AppointmentInfo) ----
    openFrame(title, url, hint) {
      this.url = url;
      this.frameHint = hint || "Close this window when you are done.";
      this.show("frame", title);
    },
    async openCancel() {
      const res = await appointmentApi.cancelReasons(this.id);
      if (!res.ok) return toast.error(res.message || "Unable to load cancel reasons.");
      const reasons = Array.isArray(res.body) ? res.body : Object.values(res.body || {});
      // the API means to append "Other" but an array union can drop it
      if (!reasons.some(r => this.isOther(r))) reasons.push("Other");
      this.cancelReasons = reasons;
      this.show("cancel", "Cancel Appointment", { reason: null, confirming: false });
    },
    async openDispose() {
      const res = await appointmentApi.disposeReasons(this.id);
      if (!res.ok) return toast.error(res.message || "Unable to load dispose reasons.");
      this.disposeReasons = Array.isArray(res.body) ? res.body : [];
      this.show("dispose", "Dispositioned Leads", { reasonId: null });
    },
    openResume() {
      this.show("resume", "Resume");
    },
    async openMembers(consumerId) {
      const res = await appointmentApi.members(consumerId);
      const list = res.ok && Array.isArray(res.body) ? res.body : [];
      if (!list.length) return toast.error(res.message || "No members found");
      this.members = list;
      // one member → singular
      this.show("members", list.length === 1 ? "Member at this Address" : "Members at this Address");
    },

    // ---- submits ----
    async submitCancel() {
      this.busy = true;
      const res = await appointmentApi.cancel(this.id, this.form.reason, this.isOther(this.form.reason) ? this.form.notes.trim() : "");
      this.busy = false;
      if (!res.ok) {
        this.form.confirming = false;
        return toast.error(res.message || "Unable to cancel the appointment.");
      }
      toast.success("Appointment has been cancelled successfully.");
      this.close();
      this.$emit("cancelled");
    },
    async submitDispose() {
      this.busy = true;
      const res = await appointmentApi.dispose(this.id, this.form.reasonId, this.disposeIsOther ? this.form.notes.trim() : "");
      this.busy = false;
      if (!res.ok) return toast.error(res.message || "Unable to dispose the appointment.");
      toast.success("Lead has been disposed successfully.");
      this.close();
      this.$emit("changed");
    },
    async submitResume() {
      this.busy = true;
      const res = await appointmentApi.resume(this.id, this.form.notes.trim());
      this.busy = false;
      if (!res.ok) return toast.error(res.message || "Unable to resume the appointment.");
      toast.success(res.message || "Appointment has been resumed.");
      this.close();
      this.$emit("changed");
    },
  },
};
</script>

<style scoped>
.apptLabel {
  display: block;
  font-size: 14px;
  color: #EA8A8A;
}
.apptInput {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #E2E5EC;
  border-radius: 8px;
  font-size: 14px;
  color: #333333;
  outline: none;
  background: #fff;
}
.apptInput:focus {
  border-color: #7695FF;
}
</style>
